import { maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { VisitorVendorStateApi } from '../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithEventService } from '../EventService/EventService';
import { VisitorVendorStateService } from './VisitorVendorStateService';

export function createVisitorVendorStateService(
  config: WithServices<[WithErrorService, WithEventService]> & {
    api: VisitorVendorStateApi;
    queryClient: QueryClient;
  }
): VisitorVendorStateService {
  const {
    api,
    queryClient,
    services: { errorService, eventService }
  } = config;
  const interceptor = errorService.withCategory('VisitorVendorStateService');

  // Listen to visitor management events and invalidate cache
  eventService.event.addListener(event => {
    if (event.type === 'VISITOR_MANAGEMENT_PROCESSED') {
      queryClient.invalidateQueries({
        queryKey: ['visitor-vendor-states']
      });
      queryClient.invalidateQueries({
        queryKey: ['visitor-vendor-states-with-bookings']
      });
    }
  });

  return {
    useList(query) {
      return useQuery(
        {
          queryKey: ['visitor-vendor-states', query],
          enabled: maybe.isPresent(query),
          queryFn: interceptor.wrap(() => api.list(query!))
        },
        queryClient
      );
    },

    useListWithBookingDetails(query) {
      return useQuery(
        {
          queryKey: ['visitor-vendor-states-with-bookings', query],
          enabled: maybe.isPresent(query),
          queryFn: interceptor.wrap(() => api.listWithBookingDetails(query!))
        },
        queryClient
      );
    },

    async manualSync(schema, bookingId) {
      const result = await interceptor.wrap(() =>
        api.manualSync(schema, bookingId)
      )();

      // Invalidate cache after manual sync
      queryClient.invalidateQueries({
        queryKey: ['visitor-vendor-states']
      });
      queryClient.invalidateQueries({
        queryKey: ['visitor-vendor-states-with-bookings']
      });

      return result;
    }
  };
}
